/**
 * Reusable UI Components Library for SplitPal Webapp
 * 
 * This module provides standardized, reusable UI components to ensure
 * consistency across the application. Always use these components instead
 * of creating new buttons/inputs from scratch.
 */

/**
 * Creates a Floating Action Button (FAB)
 * @param {Object} options - Configuration options
 * @param {string} options.text - Button text
 * @param {string} options.icon - SVG icon markup (optional)
 * @param {Function} options.onClick - Click handler
 * @param {string} options.className - Additional CSS classes (optional)
 * @returns {HTMLButtonElement} The FAB button element
 */
export function createFAB({ text, icon, onClick, className = '' }) {
    const fab = document.createElement('button');
    fab.className = `receipt-fab ${className}`.trim();

    // Apply consistent Material Design 3 styling inline
    Object.assign(fab.style, {
        position: 'fixed',
        bottom: '30px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'var(--color-primary)', // Material Design 3 Brand Color
        color: 'white',
        padding: '12px 24px',
        borderRadius: '50px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        cursor: 'pointer',
        fontWeight: '600',
        fontSize: '14px',
        zIndex: '1000', // Below modals (2000) but above regular content
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        border: 'none'
    });

    let innerHTML = '';
    if (icon) {
        innerHTML += icon;
    }
    if (text) {
        innerHTML += `<span>${text}</span>`;
    }

    fab.innerHTML = innerHTML;

    // Hover effects
    fab.addEventListener('mouseenter', () => {
        fab.style.transform = 'translateX(-50%) translateY(-2px)';
        fab.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.3)';
    });

    fab.addEventListener('mouseleave', () => {
        fab.style.transform = 'translateX(-50%)';
        fab.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.25)';
    });

    fab.addEventListener('mousedown', () => {
        fab.style.transform = 'translateX(-50%) scale(0.98)';
    });

    fab.addEventListener('mouseup', () => {
        fab.style.transform = 'translateX(-50%)';
    });

    if (onClick) {
        fab.addEventListener('click', onClick);
    }

    return fab;
}

/**
 * Standard SVG icons used throughout the app
 */
export const Icons = {
    checkmark: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="20 6 9 17 4 12"></polyline>
    </svg>`,

    edit: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
    </svg>`,

    link: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
    </svg>`,

    users: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>`,

    currency: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="12" y1="1" x2="12" y2="23"></line>
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
    </svg>`,

    verticalDots: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="1.5"></circle>
        <circle cx="12" cy="5" r="1.5"></circle>
        <circle cx="12" cy="19" r="1.5"></circle>
    </svg>`,

    trash: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="3 6 5 6 21 6"></polyline>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    </svg>`,

    plus: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>`
};

/**
 * Creates a standard menu button (3-dot vertical menu)
 * @param {Function} onClick - Click handler
 * @param {Object} options - Additional options
 * @returns {HTMLDivElement} The menu button element
 */
export function createMenuButton(onClick, options = {}) {
    const menuButton = document.createElement('div');
    menuButton.className = 'menu-button';
    menuButton.style.cssText = `
        width: ${options.size || '40px'};
        height: ${options.size || '40px'};
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        border-radius: 50%;
        transition: background-color 0.2s ease;
    `;

    menuButton.innerHTML = Icons.verticalDots;

    menuButton.addEventListener('mouseenter', () => {
        menuButton.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
    });

    menuButton.addEventListener('mouseleave', () => {
        menuButton.style.backgroundColor = 'transparent';
    });

    if (onClick) {
        menuButton.addEventListener('click', (e) => {
            e.stopPropagation();
            onClick(e);
        });
    }

    return menuButton;
}

/**
 * Creates a menu item for use in pull-up menus
 * @param {Object} options - Configuration options
 * @param {string} options.icon - SVG icon markup
 * @param {string} options.label - Menu item text
 * @param {Function} options.onClick - Click handler
 * @returns {HTMLDivElement} The menu item element
 */
export function createMenuItem({ icon, label, onClick }) {
    const menuItem = document.createElement('div');
    menuItem.className = 'menu-item';
    menuItem.style.cssText = `
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 16px;
        cursor: pointer;
        border-radius: 8px;
        transition: background-color 0.2s ease;
    `;

    menuItem.innerHTML = `
        <div style="color: #49454F; flex-shrink: 0;">${icon}</div>
        <div style="font-size: 16px; font-weight: 500; color: #1D1B20;">${label}</div>
    `;

    menuItem.addEventListener('mouseenter', () => {
        menuItem.style.backgroundColor = 'rgba(var(--color-primary-rgb), 0.08)';
    });

    menuItem.addEventListener('mouseleave', () => {
        menuItem.style.backgroundColor = 'transparent';
    });

    if (onClick) {
        menuItem.addEventListener('click', onClick);
    }

    return menuItem;
}

/**
 * Creates a standard text input field
 * @param {Object} options - Configuration options
 * @param {string} options.label - Input label
 * @param {string} options.value - Initial value
 * @param {string} options.placeholder - Placeholder text
 * @param {number} options.maxLength - Maximum length
 * @param {string} options.type - Input type (default: 'text')
 * @returns {Object} Object containing { container, input, label }
 */
export function createTextInput({ label, value = '', placeholder = '', maxLength, type = 'text' }) {
    const container = document.createElement('div');
    container.style.padding = '10px 0';

    const labelEl = document.createElement('label');
    labelEl.textContent = label;
    labelEl.style.display = 'block';
    labelEl.style.marginBottom = '8px';
    labelEl.style.fontWeight = '600';
    labelEl.style.fontSize = '14px';

    const input = document.createElement('input');
    input.type = type;
    input.value = value;
    input.placeholder = placeholder;
    if (maxLength) input.maxLength = maxLength;
    input.style.width = '100%';
    input.style.padding = '12px';
    input.style.fontSize = '16px';
    input.style.border = '1px solid #ddd';
    input.style.borderRadius = '8px';
    input.style.boxSizing = 'border-box';

    container.appendChild(labelEl);
    container.appendChild(input);

    return { container, input, label: labelEl };
}

const FLUTTER_AVATAR_COLORS = [
    '#20B2AA', '#48D1CC', '#DDA0DD', '#DA70D6',
    '#BA55D3', '#9370DB', '#5F9EA0', '#9B87F5',
    '#00CED1', '#40E0D0', '#AB82C5', '#9F7AEA',
];

function getAvatarColor(str) {
    if (!str) return FLUTTER_AVATAR_COLORS[0];
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return FLUTTER_AVATAR_COLORS[Math.abs(hash) % FLUTTER_AVATAR_COLORS.length];
}

/**
 * Creates a participant avatar (circular with initial)
 * @param {string} name - Participant name
 * @param {Object} options - Additional options
 * @returns {HTMLDivElement} The avatar element
 */
export function createParticipantAvatar(name, options = {}) {
    const avatar = document.createElement('div');
    const initial = name.charAt(0).toUpperCase();
    const size = options.size || '40px';
    const bgColor = options.backgroundColor || getAvatarColor(name);

    avatar.style.cssText = `
        width: ${size};
        height: ${size};
        border-radius: 50%;
        background-color: ${bgColor};
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: ${options.fontSize || '16px'};
    `;

    avatar.textContent = initial;

    return avatar;
}

/**
 * Creates a standard primary button
 * @param {Object} options - Configuration options
 * @param {string} options.text - Button text
 * @param {Function} options.onClick - Click handler
 * @param {boolean} options.disabled - Whether button is disabled
 * @returns {HTMLButtonElement} The button element
 */
export function createPrimaryButton({ text, onClick, disabled = false }) {
    const button = document.createElement('button');
    button.textContent = text;
    button.disabled = disabled;
    button.style.cssText = `
        background-color: var(--color-primary);
        color: white;
        padding: 12px 24px;
        border-radius: 8px;
        border: none;
        font-size: 16px;
        font-weight: 600;
        cursor: ${disabled ? 'not-allowed' : 'pointer'};
        opacity: ${disabled ? '0.5' : '1'};
        transition: background-color 0.2s ease;
    `;

    if (!disabled) {
        button.addEventListener('mouseenter', () => {
            button.style.backgroundColor = 'var(--color-primary-hover)';
        });

        button.addEventListener('mouseleave', () => {
            button.style.backgroundColor = 'var(--color-primary)';
        });
    }

    if (onClick) {
        button.addEventListener('click', onClick);
    }

    return button;
}
